/**
 * Sawyer 06 beds-lockers-runcard (B4) — "Bed & locker" at the top of the
 * run-card editor: tonight's bed (read-only) and this dog's locker for this
 * stay.
 *
 * Sawyer, 2026-10-23 (memo2 [05:01]-[06:01]): lockers "default to a number
 * and then as part of like the selection process ... you can change it",
 * and "when it's two dogs, we put the two ... in my locker".
 *
 * Counter Test: Sawyer, leash in hand, taps Edit card, sees "Locker 7 ·
 * From Dog 4's default", taps Save to card, prints. Or types "Box A",
 * ticks "Same locker for all 2 dogs on this booking", Save, Print.
 *
 * The section loads on its own (`useStayLocker`), independent of the
 * editor's edits read, and saves FIRST when the editor's one primary
 * "Save to card" is pressed: if the locker save fails nothing else is sent,
 * the typed value stays and the server's sentence shows under the field.
 * An older server (no lockers route) gets one honest line and no input;
 * the rest of the editor works (principle 5: never a control that cannot
 * save).
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { humanizeApiError, readSession } from '../../lib/api.js';
import { effectiveRolesOf } from '../../lib/rbac-matrix.js';
import { rolesCanAccessPath } from '../../lib/route-permissions.js';
import {
  fetchStayLockers,
  saveStayLocker,
  type StayLockerInput,
  type StayLockersView,
} from '../../lib/stay-lockers-api.js';
import './StayLockerSection.css';

/** Mirrors the server's MAX_LOCKER_LENGTH. */
export const MAX_LOCKER_LENGTH = 40;
export const LOCKER_LOAD_ERROR = "Couldn't load the locker. You can still edit and print the card.";
export const LOCKER_FIELD_HINT = 'Use the Locker field above so it prints at the top.';

export interface LockerDraft {
  /** What the input holds. */
  readonly value: string;
  /** "Same locker for all N dogs on this booking". */
  readonly applyAll: boolean;
  /** "Use the bed's default" was chosen: send null. */
  readonly useBedDefault: boolean;
}

type DogRow = StayLockersView['dogs'][number];

/**
 * The PUT body for a draft, or null when nothing changed. A blank input or
 * "Use the bed's default" sends `locker: null` (this stay follows the bed
 * again); a value equal to what already prints is no change unless it is
 * being applied to every dog.
 */
export function lockerBodyFromDraft(draft: LockerDraft, dog: DogRow, multiDog: boolean): StayLockerInput | null {
  const typed = draft.value.trim();
  const locker = draft.useBedDefault || typed === '' ? null : typed;
  const all = multiDog && draft.applyAll;
  const unchanged = draft.useBedDefault
    ? dog.stay_locker === null
    : locker === null
      ? dog.stay_locker === null
      : locker === (dog.locker ?? null);
  if (unchanged && !all) return null;
  return all ? { scope: 'booking', locker } : { scope: 'dog', dogId: dog.dog_id, locker };
}

/** The caption under the input. */
export function lockerCaption(dog: DogRow, view: StayLockersView): string {
  if (dog.locker_source === 'stay') return 'Set for this stay';
  if (dog.locker_source === 'bed_default' && view.bed) return `From ${view.bed.name}'s default`;
  return 'No locker set';
}

export type StayLockerState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly view: StayLockersView; readonly dog: DogRow };

export interface UseStayLocker {
  readonly state: StayLockerState;
  readonly draft: LockerDraft;
  readonly setDraft: (patch: Partial<LockerDraft>) => void;
  readonly saveError: string | null;
  /** Saves when the draft changed. false = it failed (the error is set); true = saved or nothing to save. */
  readonly saveIfChanged: () => Promise<boolean>;
}

const EMPTY_DRAFT: LockerDraft = { value: '', applyAll: false, useBedDefault: false };

export function useStayLocker(args: {
  apiUrl: string;
  token: string;
  bookingId: string;
  dogId: string;
  night?: string;
}): UseStayLocker {
  const { apiUrl, token, bookingId, dogId, night } = args;
  const [state, setState] = useState<StayLockerState>({ status: 'loading' });
  const [draft, setDraftState] = useState<LockerDraft>(EMPTY_DRAFT);
  const [saveError, setSaveError] = useState<string | null>(null);

  const adopt = useCallback(
    (view: StayLockersView): boolean => {
      const dog = view.dogs.find((d) => d.dog_id === dogId);
      if (!dog) return false;
      setState({ status: 'ready', view, dog });
      setDraftState({ value: dog.locker ?? '', applyAll: false, useBedDefault: false });
      return true;
    },
    [dogId],
  );

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void (async () => {
      try {
        const view = await fetchStayLockers(apiUrl, token, bookingId, night);
        if (cancelled) return;
        if (!adopt(view)) setState({ status: 'error' });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token, bookingId, night, adopt]);

  const setDraft = useCallback((patch: Partial<LockerDraft>) => {
    setDraftState((d) => ({ ...d, ...patch }));
    setSaveError(null);
  }, []);

  const saveIfChanged = useCallback(async (): Promise<boolean> => {
    if (state.status !== 'ready') return true;
    const body = lockerBodyFromDraft(draft, state.dog, state.view.dogs.length > 1);
    if (body === null) return true;
    try {
      const view = await saveStayLocker(apiUrl, token, bookingId, body, night);
      adopt(view);
      return true;
    } catch (err) {
      setSaveError(humanizeApiError(err instanceof Error ? err.message : 'unknown'));
      return false;
    }
  }, [state, draft, apiUrl, token, bookingId, night, adopt]);

  return { state, draft, setDraft, saveError, saveIfChanged };
}

export function StayLockerSection({
  locker,
  dogName,
  disabled,
}: {
  readonly locker: UseStayLocker;
  readonly dogName: string;
  readonly disabled: boolean;
}): JSX.Element {
  const idBase = useId();
  const { state, draft, setDraft, saveError } = locker;

  if (state.status === 'loading') {
    return (
      <section className="ferro-stay-locker" aria-busy="true" data-testid="run-card-editor-locker-loading">
        <h3 className="ferro-run-card-editor__legend">Bed &amp; locker</h3>
        <span className="ferro-run-card-editor__skeleton-line" />
        <span className="ferro-run-card-editor__skeleton-line ferro-run-card-editor__skeleton-line--short" />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="ferro-stay-locker" data-testid="run-card-editor-locker-error">
        <h3 className="ferro-run-card-editor__legend">Bed &amp; locker</h3>
        <p className="ferro-run-card-editor__hint">{LOCKER_LOAD_ERROR}</p>
      </section>
    );
  }

  const { view, dog } = state;
  const multi = view.dogs.length > 1;
  const canUseDefault =
    dog.stay_locker !== null && view.bed?.default_locker != null && view.locker_follows_bed && !draft.useBedDefault;
  const canOpenLodging = rolesCanAccessPath(effectiveRolesOf(readSession().staff), '/calendar/lodging');
  const inputId = `${idBase}-locker`;
  const typed = draft.value.trim();
  const caption = draft.useBedDefault && view.bed
    ? `From ${view.bed.name}'s default`
    : typed !== '' && typed !== (dog.locker ?? '')
      ? 'Set for this stay when you save'
      : lockerCaption(dog, view);

  return (
    <section className="ferro-stay-locker" data-testid="run-card-editor-locker-section">
      <h3 className="ferro-run-card-editor__legend">Bed &amp; locker</h3>
      <p className="ferro-stay-locker__bed" data-testid="run-card-editor-bed">
        {view.bed ? (
          <>
            <span className="ferro-stay-locker__label">Bed</span>{' '}
            <strong>{view.bed.name}</strong>
            {view.bed.size_class ? ` · ${view.bed.size_class}` : ''}
          </>
        ) : (
          <>
            No bed for tonight.
            {canOpenLodging ? (
              <>
                {' '}
                <a
                  className="ferro-stay-locker__link"
                  href={`/calendar/lodging?date=${encodeURIComponent(view.night)}`}
                  data-testid="run-card-editor-assign-bed"
                >
                  Assign on the Lodging calendar
                </a>
              </>
            ) : null}
          </>
        )}
      </p>
      <label className="ferro-run-card-editor__label" htmlFor={inputId}>
        Locker
      </label>
      <input
        id={inputId}
        className="ferro-run-card-editor__input"
        maxLength={MAX_LOCKER_LENGTH}
        value={draft.value}
        placeholder="e.g. 12 or Box A"
        disabled={disabled}
        aria-describedby={`${inputId}-caption`}
        aria-invalid={saveError ? true : undefined}
        onChange={(e) => setDraft({ value: e.target.value, useBedDefault: false })}
        data-testid="run-card-editor-locker"
      />
      <p id={`${inputId}-caption`} className="ferro-run-card-editor__hint" data-testid="run-card-editor-locker-caption">
        {caption}
      </p>
      {saveError ? (
        <p className="ferro-stay-locker__error" role="alert" data-testid="run-card-editor-locker-error-msg">
          {saveError}
        </p>
      ) : null}
      {canUseDefault && view.bed ? (
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-stay-locker__default"
          disabled={disabled}
          onClick={() => setDraft({ value: view.bed?.default_locker ?? '', useBedDefault: true })}
          data-testid="run-card-editor-locker-default"
        >
          Use the bed&apos;s default ({view.bed.default_locker})
        </button>
      ) : null}
      {multi ? (
        <label className="ferro-run-card-editor__save ferro-stay-locker__all">
          <input
            type="checkbox"
            checked={draft.applyAll}
            disabled={disabled}
            onChange={(e) => setDraft({ applyAll: e.target.checked })}
            data-testid="run-card-editor-locker-all"
          />
          <span>
            Same locker for all {view.dogs.length} dogs on this booking
          </span>
        </label>
      ) : null}
      <p className="ferro-run-card-editor__hint">Prints big at the top of {dogName}&apos;s card.</p>
    </section>
  );
}
